import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  MapPin,
  Box,
  ArrowRight,
  Rss,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Camera
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '0.0.0.0';

export interface JobImage {
  filePath: string;
}

export interface Job {
  id: string;
  title: string;
  pickupDistrict: string;
  deliveryDistrict: string;
  priceAmount: number;
  createdAt: string;
  loadCount: number;
  cargoWeight?: number;
  cargoUnit?: string;
  cargoWeightKg?: number;
  remainingLoads?: number; // New computed field
  images?: JobImage[];
}

function formatWeight(w?: number, u?: string, kg?: number): string {
  if (w == null || u == null || kg == null) return '';
  const n = Number(kg);
  if (isNaN(n)) return '';
  const kgText = `(${n.toFixed(1)}kg)`;
  return u === 'TONNE'
    ? `• ${w} Tonnes ${kgText}`
    : `• ${w} kg`;
}

// Named export
export const JobCard: React.FC<{ job: Job }> = ({ job }) => {
  const images = React.useMemo(() => Array.isArray(job.images) ? job.images : [], [job.images]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const processedImages = React.useMemo(() => {
    images.forEach((img) => {
      const i = new Image();
      i.src = `${API_BASE}/uploads/${img.filePath}`;
    });
    return images;
  }, [images]);

  return (
    <>
      <Card className="w-full hover-lift flex flex-col group transition-all duration-300">
        {images[0] && (
          <div
            className="relative overflow-hidden rounded-t-lg cursor-pointer"
            onClick={() => setOpen(true)}
          >
            <img
              loading="lazy"
              src={`${API_BASE}/uploads/${images[0].filePath}`}
              alt={job.title}
              className="w-full h-36 sm:h-40 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            {images.length > 1 && (
              <Badge variant="secondary" className="absolute bottom-2 right-2 flex items-center gap-1 text-[10px] px-1.5 h-5">
                <Camera size={10} />
                {images.length}
              </Badge>
            )}
          </div>
        )}

        <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-4">
          <CardTitle className="text-base sm:text-lg line-clamp-1">{job.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4 flex flex-col flex-grow text-xs sm:text-sm p-3 pt-0 sm:p-6 sm:pt-0">
          <div className="flex items-center gap-2 font-medium">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="truncate">{job.pickupDistrict}</span>
            <ArrowRight size={12} className="flex-shrink-0" />
            <span className="truncate">{job.deliveryDistrict}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Box className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="truncate">{job.loadCount} Total Load(s) {formatWeight(job.cargoWeight, job.cargoUnit, job.cargoWeightKg)}</span>
          </div>
          {/* Progress Bar / Availability Badge */}
          {job.remainingLoads !== undefined && job.remainingLoads > 0 && (
            <div className="w-full bg-secondary/30 rounded-full h-2 mt-1 relative overflow-hidden">
              <div
                className="bg-primary h-full rounded-full transition-all duration-500"
                style={{ width: `${((job.loadCount - job.remainingLoads) / job.loadCount) * 100}%` }}
              />
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-bold text-foreground">
                {job.remainingLoads} / {job.loadCount} Available
              </span>
            </div>
          )}
          {/* Fallback if calculating or fresh */}
          {(job.remainingLoads === undefined || job.remainingLoads === job.loadCount) && (
            <Badge variant="outline" className="w-fit text-[10px] h-5 border-green-500 text-green-600 bg-green-50">
              {job.loadCount} Loads Available
            </Badge>
          )}
          <div className="flex items-center gap-2 text-primary font-bold text-base sm:text-lg">
            <Rss className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            MWK {job.priceAmount.toLocaleString()}
            <span className="text-[10px] sm:text-xs text-muted-foreground font-normal">per load</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
            <Calendar size={10} className="sm:w-3 sm:h-3" />
            {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
          </div>
          <div className="pt-2 sm:pt-4 mt-auto">
            <Button asChild variant="premium" size="sm" className="w-full text-sm h-10 shadow-sm active:scale-95 transition-transform">
              <Link to={`/jobs/${job.id}`}>View & Secure Job</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {images[0] && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl p-2">
            <DialogHeader>
              <DialogTitle>
                {job.title} ({idx + 1}/{images.length})
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <img
                loading="lazy"
                src={`${API_BASE}/uploads/${images[idx].filePath}`}
                alt={`${job.title} ${idx + 1}`}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
              {images.length > 1 && (
                <>
                  <Button
                    size="icon" variant="secondary"
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    onClick={() => setIdx(i => (i - 1 + images.length) % images.length)}
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    size="icon" variant="secondary"
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                    onClick={() => setIdx(i => (i + 1) % images.length)}
                  >
                    <ChevronRight />
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Default export
export default JobCard;
